import { Component, OnInit, ViewEncapsulation, computed, inject } from "@angular/core";
import {
    AssetManagementService,
    AssetLifecycle,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { Pages } from "@constants/pages/Pages";
import { formatIsoDate } from "@methods/CommonMethods";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import { finalize, firstValueFrom } from "rxjs";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixPageFooterComponent } from "@app/shared/cerrix-page-footer/cerrix-page-footer.component";
import { WorkflowStepItemModel } from "@app/shared/cerrix-workflow-bar/models/workflow-step-item";
import { WorkflowStepStatus } from "@app/shared/cerrix-workflow-bar/models/workflow-step-status.enum";
import { CerrixHeaderComponent } from "@app/shared/organisms/cerrix-header/cerrix-header.component";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ModuleType } from "@enums/ModuleType";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { CerrixTextareaGroupComponent } from "@app/shared/molecules/cerrix-textarea-group/cerrix-textarea-group.component";
import { CerrixDatepickerGroupComponent } from "@app/shared/molecules/cerrix-datepicker-group/cerrix-datepicker-group.component";
import { CerrixTreeItemPickerSingleComponent } from "@app/shared/organisms/cerrix-tree-item-picker-single/cerrix-tree-item-picker-single.component";
import { IdGuidNameCombination } from "@models/generic/IdGuidNameCombination";
import { HttpClient } from "@angular/common/http";
import { CerrixUserSelectComponent } from "@app/shared/organisms/cerrix-user-select/cerrix-user-select.component";
import { CerrixUserTypePickerConfig } from "@app/shared/organisms/cerrix-user-type-picker/models/cerrix-user-type-picker-config";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";

@Component({
    selector: "app-assetmanagement-detail-register",
    templateUrl: "./assetmanagement-detail-register.component.html",
    styleUrls: ["./assetmanagement-detail-register.component.scss"],
    imports: [
        CerrixPageFooterComponent,
        CerrixButtonComponent,
        CerrixHeaderComponent,
        CerrixTextareaGroupComponent,
        CerrixDatepickerGroupComponent,
        CerrixTreeItemPickerSingleComponent,
        CerrixUserSelectComponent,
        MenuSystemDirective,
        CerrixInputValidationRequiredDirective,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [CerrixInputValidationRegistryService],
    encapsulation: ViewEncapsulation.None,
})
export class AssetManagementDetailRegisterComponent implements OnInit {
    tab: TabModel;
    id: string;

    model: AssetManagementModel;

    private workflowGeneralInformation = "generalInformation";
    private workflowAssetType = "assetType";
    private workflowOverview = "Overview";
    public workflowSteps: WorkflowStepItemModel[] = [];

    public breadcrumbs: string[] = [];

    public saveButtonEnabled = true;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _toastService = inject(ToastrService);
    private _promptService = inject(CerrixPromptService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _standingDataDS = inject(StandingdataDataService);
    private _translation = injectTranslation(scope);
    private _http = inject(HttpClient);

    public organizationOwnerList: CerrixTreeItem[] = [];

    public users: IdGuidNameCombination[] = [];

    protected ownerTypes: CerrixUserTypePickerConfig[] = [];
    protected ownerValue: Record<string, string[]> = { owner: [] };

    protected validationErrors = computed(() => {
        return this._validationRegistry.hasAnyErrors();
    });

    async ngOnInit() {
        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();

        this.workflowSteps = [
            {
                id: this.workflowGeneralInformation,
                name: this._translation.t(
                    "detail.workflowGeneralInformation",
                ),
                status: WorkflowStepStatus.Active,
                infoTitle: "",
            },
            {
                id: this.workflowAssetType,
                name: this._translation.t("detail.workflowAssetType"),
                status: WorkflowStepStatus.Disabled,
                infoTitle: "",
            },
            {
                id: this.workflowOverview,
                name: this._translation.t("detail.workflowOverview"),
                status: WorkflowStepStatus.Disabled,
                infoTitle: "",
            },
        ];

        this.breadcrumbs = [
            this._translation.t("detail.breadcrumbWorkspace"),
        ];

        this.model = new AssetManagementModel();
        this.model.name = "";
        this.model.description = "";
        this.model.lifecycle = { dateInUse: null, dateOutOfUse: null };
        this.model.ownershipInformation = { owningOrganizationId: null, ownerId: null };
        this.breadcrumbs.push(
            this._translation.t("detail.breadcrumbNewAsset"),
        );

        this.organizationOwnerList = await firstValueFrom(
            this._standingDataDS.getOrganizationsIncludingSelected(
                ModuleType.AssetManagement,
                null,
                true,
            ),
        );

        this.users = await this.getUsers();
        this.ownerTypes = [
            {
                key: "owner",
                label: this._translation.t(
                    "edit-ownership-information.ownerLabel",
                ),
                users: this.users,
            },
        ];

        this.tab.showLoader = false;
    }

    private async getUsers(): Promise<IdGuidNameCombination[]> {
        let params = "";

        if (this.model.ownershipInformation.ownerId) {
            params = `?selected=${this.model.ownershipInformation.ownerId}`;
        }

        return await firstValueFrom(
            this._http.get<IdGuidNameCombination[]>(`/api/users/list` + params),
        );
    }

    protected onOwnerIdChange(ownerId: string): void {
        this.model.ownershipInformation = {
            ...this.model.ownershipInformation,
            ownerId,
        };
        this.ownerValue = { owner: ownerId ? [ownerId] : [] };
    }

    async save() {
        if (!this.validateFields()) {
            return;
        }

        if (this.model.reference === undefined) {
            this.saveButtonEnabled = false;
            const savingPrompt = this._promptService.loader(
                this._translation.t("detail.savingChanges"),
            );

            const lifecycleModel: AssetLifecycle = {
                dateInUse: formatIsoDate(this.model.lifecycle.dateInUse),
                dateOutOfUse: formatIsoDate(this.model.lifecycle.dateOutOfUse),
            };
            const storedAsset = await firstValueFrom(
                this._ds
                    .registerAssetVbeta({
                        assetName: this.model.name,
                        description: this.model.description,
                        organizationId: this.model.ownershipInformation.owningOrganizationId,
                        ownerId: this.model.ownershipInformation?.ownerId ?? null,
                        lifecycle: lifecycleModel,
                    })
                    .pipe(finalize(() => savingPrompt.close())),
            );

            this.tab.id = storedAsset.guid;
            this._tabService.refresh(this._pages.AssetManagementOverview);
            this._toastService.success(
                this._translation.t("detail.updateCompleted"),
            );
        }
    }

    public cancel(): void {
        this.tab.close();
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._translation.t(
                                "detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._translation.t("detail.saveFailed"),
            );
            return false;
        }
        return true;
    }
}
